import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import {
  addDays,
  isBefore,
  endOfDay,
  isValid,
  format,
  getHours,
  isSameDay,
  parse,
} from 'date-fns'
import { Grid, IconButton } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DatePicker from 'ui/partials/DatePicker'
import { DateString } from 'config/format'
import { ReservationDeadlineTimings } from 'types/med'
import { jstNewDate } from 'ui/utils/jstDatetime'

export type ReservationDateSelectProps = {
  selectedDate: Date
  onSelect: (date: Date) => void
  className?: string
  minDate?: Date
  minDateMessage?: string
  maxDate?: Date
  maxDateMessage?: string
  reservableDates?: { reservable_date: string }[]
  onChangeCalenderMonth?: (date: Date) => void
  reservableDeadlineTiming?: ReservationDeadlineTimings | null
}

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
}))

const ReservationDateSelect: React.FC<ReservationDateSelectProps> = ({
  selectedDate,
  onSelect,
  className,
  minDate,
  minDateMessage,
  maxDate,
  maxDateMessage,
  reservableDates,
  onChangeCalenderMonth,
  reservableDeadlineTiming,
}: ReservationDateSelectProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const [date, setDate] = useState(selectedDate)
  const [mounted, setMounted] = useState(false)
  const addDateHandler = (add: number) => {
    setDate(addDays(date, add))
  }

  const onSelectRef = useRef(onSelect)

  useEffect(() => {
    if (!isValid(date)) return
    mounted ? onSelectRef.current(date) : setMounted(true) // NOTE: 初回に発火するのを防ぐ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date])

  useEffect(() => {
    // NOTE: カレンダー操作以外で日付が変更された時にonSelectが発火しないように
    if (date && !isSameDay(date, selectedDate)) {
      setMounted(false)
      setTimeout(() => {
        setDate(selectedDate)
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate])

  const leftButtonDisabled =
    minDate && isBefore(endOfDay(addDays(date, -1)), minDate)

  const rightButtonDisabled = maxDate && !isBefore(addDays(date, 1), maxDate)

  const now = jstNewDate
  const todayString = format(now, DateString)

  const reservableDateArray = useMemo(() => {
    const tomorrowString = format(addDays(now, 1), DateString)
    const isAm: boolean = getHours(now) < 12
    const inputStringArray = reservableDates?.map(
      ({ reservable_date }) => reservable_date
    )
    if (
      reservableDeadlineTiming === 'day_before' ||
      (reservableDeadlineTiming === 'day_before_12:00' && isAm)
    ) {
      return inputStringArray?.filter((string) => string !== todayString)
    }
    if (reservableDeadlineTiming === 'day_before_12:00') {
      return inputStringArray?.filter(
        (string) => string !== todayString && string !== tomorrowString
      )
    }
    return inputStringArray
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservableDates, reservableDeadlineTiming, todayString])

  return (
    <div className={cx(classes.root, className)}>
      <IconButton
        onClick={() => addDateHandler(-1)}
        disabled={leftButtonDisabled}
        size="large"
      >
        <ChevronLeftIcon color={leftButtonDisabled ? 'disabled' : 'action'} />
      </IconButton>
      <Grid container justifyContent="space-around">
        <DatePicker
          format={t('datetime.M月d日（E）')}
          onDateChange={(d) => {
            typeof date.getMonth === 'function' && setDate(d)
          }}
          date={date}
          minDate={minDate}
          minDateMessage={minDateMessage}
          maxDate={maxDate}
          maxDateMessage={maxDateMessage}
          invalidDateMessage={t('日付の形式が不正です')}
          disableUnderline
          reservableDates={reservableDateArray?.map((date) =>
            parse(date, DateString, new Date())
          )}
          onChangeCalenderMonth={onChangeCalenderMonth}
        />
      </Grid>
      <IconButton
        onClick={() => addDateHandler(1)}
        disabled={rightButtonDisabled}
        size="large"
      >
        <ChevronRightIcon color={rightButtonDisabled ? 'disabled' : 'action'} />
      </IconButton>
    </div>
  )
}

export default ReservationDateSelect
